import { ReactNode, useId, useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./DesignStudioAdvancedEditor.module.scss";
import { DesignStudioColorField } from "./DesignStudioColorField";
import { IDesignStudioState } from "../hooks/useDesignStudio";
import { DefaultToggle } from "../../../../../../common/components/forms/inputs/toggle/DefaultToggle";
import {
  Default_Accent_From,
  Default_Accent_Text,
  Default_Accent_To,
} from "../../../../../../common/infrastructure/utilities/colorUtility";
import {
  formatContrastRatio,
  getAccentLegibility,
  getButtonLegibility,
  IDesignStudioContrast,
} from "../utils/designStudioPreviewUtility";

interface IDesignStudioAdvancedEditorProps {
  studio: IDesignStudioState;
}

interface IGroupProps {
  title: string;
  hint: string;
  children: ReactNode;
}

const Group = ({ title, hint, children }: IGroupProps) => {
  const titleId = useId();

  return (
    <section className={styles.group} aria-labelledby={titleId}>
      <header className={styles.group_header}>
        <h3 id={titleId} className={styles.group_title}>
          {title}
        </h3>
        <p className={styles.group_hint}>{hint}</p>
      </header>
      {children}
    </section>
  );
};

const Verdict = ({ passes }: { passes: boolean }) => (
  <span className={classNames(styles.verdict, { [styles.verdict_weak]: !passes })}>
    <span className={styles.verdict_dot} aria-hidden />
    {passes ? "Добър контраст" : "Слаб контраст"}
  </span>
);

const Ratio = ({ contrast }: { contrast: IDesignStudioContrast }) => (
  <span className={classNames(styles.ratio, { [styles.ratio_weak]: !contrast.passes })}>
    {formatContrastRatio(contrast.ratio)}
  </span>
);

export const DesignStudioAdvancedEditor = ({ studio }: IDesignStudioAdvancedEditorProps) => {
  const id = useId();
  const { colors, hasOwnAccent, autoButtonText, autoAccentText, setColor } = studio;

  const buttonLegibility = useMemo(() => getButtonLegibility(colors), [colors]);
  const accentLegibility = useMemo(() => getAccentLegibility(colors), [colors]);

  // Without an accent of its own the studio shows the built-in purple → pink; the pickers
  // show it too, greyed, until a colour is picked.
  const accentFrom = hasOwnAccent ? colors.accentColorFrom : Default_Accent_From;
  const accentTo = hasOwnAccent ? colors.accentColorTo : Default_Accent_To;
  const accentText = hasOwnAccent && colors.accentTextColor ? colors.accentTextColor : Default_Accent_Text;

  return (
    <div className={styles.root}>
      <Group title="Фон" hint="Оцветяват тъмния фон, менюто и хедъра на имейлите">
        <div className={styles.fields}>
          <DesignStudioColorField
            label="Основен"
            name="Фон - основен цвят"
            value={colors.backgroundColorPrimary}
            onChange={(hex) => setColor("backgroundColorPrimary", hex)}
          />
          <DesignStudioColorField
            label="Второстепенен"
            name="Фон - второстепенен цвят"
            value={colors.backgroundColorSecondary}
            onChange={(hex) => setColor("backgroundColorSecondary", hex)}
          />
        </div>
      </Group>

      <Group title="Бутон" hint="Основните бутони, като „Запази“ и „Добавяне“">
        <div className={styles.fields}>
          <DesignStudioColorField
            label="Фон"
            name="Бутон - цвят на фон"
            value={colors.buttonBackgroundColor}
            onChange={(hex) => setColor("buttonBackgroundColor", hex)}
          />
          <DesignStudioColorField
            label="Текст"
            name="Бутон - цвят на текст"
            value={colors.buttonTextColor}
            disabled={autoButtonText}
            onChange={(hex) => setColor("buttonTextColor", hex)}
          />
        </div>

        <div className={styles.group_footer}>
          <DefaultToggle
            standalone
            name={`${id}-auto-button-text`}
            label="Автоматичен текст"
            checked={autoButtonText}
            onChange={studio.setAutoButtonText}
            width="auto"
            className={styles.toggle}
          />
          <span className={styles.legibility}>
            <Ratio contrast={buttonLegibility} />
            <Verdict passes={buttonLegibility.passes} />
          </span>
        </div>
      </Group>

      <Group
        title="Акцент"
        hint="Бутонът „Потвърди“, избраният ден и час, превключвателите и отметките"
      >
        {!hasOwnAccent && (
          <p className={styles.notice}>
            <span className={styles.notice_swatch} aria-hidden />
            Вграденият акцент на Planny
          </p>
        )}

        <div className={classNames(styles.fields, styles.fields_three)}>
          <DesignStudioColorField
            label="Начало"
            name="Акцент - начален цвят"
            value={accentFrom}
            inherited={!hasOwnAccent}
            onChange={(hex) => setColor("accentColorFrom", hex)}
          />
          <DesignStudioColorField
            label="Край"
            name="Акцент - краен цвят"
            value={accentTo}
            inherited={!hasOwnAccent}
            onChange={(hex) => setColor("accentColorTo", hex)}
          />
          <DesignStudioColorField
            label="Текст"
            name="Акцент - цвят на текст"
            value={accentText}
            inherited={!hasOwnAccent}
            disabled={autoAccentText}
            onChange={(hex) => setColor("accentTextColor", hex)}
          />
        </div>

        <div className={styles.group_footer}>
          <DefaultToggle
            standalone
            name={`${id}-auto-accent-text`}
            label="Автоматичен текст"
            checked={autoAccentText}
            onChange={studio.setAutoAccentText}
            width="auto"
            className={styles.toggle}
          />
          <span className={styles.legibility}>
            <Verdict passes={accentLegibility.passes} />
          </span>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.action} onClick={studio.applyButtonToAccent}>
            <span
              className={classNames(styles.action_swatch, styles.action_swatch_button)}
              aria-hidden
            />
            Като бутона
          </button>
          <button
            type="button"
            className={styles.action}
            onClick={studio.clearAccent}
            disabled={!hasOwnAccent}
          >
            <span
              className={classNames(styles.action_swatch, styles.action_swatch_builtin)}
              aria-hidden
            />
            Вграден акцент
          </button>
        </div>
      </Group>
    </div>
  );
};
